/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import { useLazyQuery, useMutation } from "@apollo/client";
import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  TextInput,
  View,
  ViewStyle,
  TouchableOpacity,
  Platform,
} from "react-native";
import { Prompt } from "react-router";
import { DatePicker } from "baseui/datepicker";
import { info } from "react-icons-kit/feather/info";
import { TimePicker } from "baseui/timepicker";
import { TimezonePicker } from "baseui/timezonepicker";
import ChevronDown from "baseui/icon/chevron-down";
import ChevronUp from "baseui/icon/chevron-up";
import { Select, SelectOverrides } from "baseui/select";
import Icon from "react-icons-kit";
import Toggle from "react-toggle";
import { questionCircle } from "react-icons-kit/fa/questionCircle";
import { videoCamera } from "react-icons-kit/fa/videoCamera";
import ReactTooltip from "react-tooltip";
import { DragDropContext, Droppable } from "react-beautiful-dnd";

import { useDispatch, useSelector } from "react-redux";
import { Redirect } from "react-router-dom";
import { ActionCreators } from "actions";
import { RootState, SelfObject } from "store/types";

import {
  BackExploreBar,
  Button,
  ConfirmDiscardModal,
  DescriptionTextField,
  LoadingView,
  Text,
  styles,
} from "UpduoLibrary";
import {
  skaleDarkGray,
  skaleOrange,
  skalePrimaryBlue,
  skaleRed,
  skaleWebBorderGray,
  skaleWebBackgroundGray,
  skaleWhite,
  skaleWebTextBackgroundLightGray,
  upduoPrimaryBlue,
  upduoLighterGray,
  upduoPrimaryDark,
  upduoSecondLightestGray,
  upduoMediumDarkGray,
  upduoPrimaryBlack,
} from "config/UpduoColors";
import { useEventListener } from "config/Hooks";
import {
  allowedMatchAlgorithmList,
  getNodes,
  convertDateTypeToTimeString,
  reorder,
} from "config/Utilities";
import {
  GET_COHORTS,
  GET_CURRICULUM,
  GET_SUMMIT_DETAIL,
} from "graphql/internal/Queries";
import {
  CREATE_SUMMIT,
  EDIT_SUMMIT,
  UPDATE_TIME_WINDOW,
  DEACTIVATE_TIME_WINDOW,
} from "graphql/internal/Mutations";
import {
  CreateSummitMutationResponse,
  EditSummitMutationResponse,
  UpdateTimeWindowResponse,
  TimeWindowType,
  DeactivateTimeWindowResponse,
  SkillModuleType,
  CohortType,
} from "graphql/internal/generated";
import * as UpduoAnalytics from "config/UpduoAnalytics";

import TimeWindowIntroModal from "./TimeWindowIntroModal";
import TimeWindowEditRow from "./TimeWindowEditRow";
import { RecursivePartial } from "config/UtilityTypes";

export default function SummitDetailEditRoute({ match, history }) {
  // The summit detail/editing route component
  const selfObject: SelfObject = useSelector(
    (state: RootState) => state.selfObject
  );
  const dispatch = useDispatch();
  const authToken = useSelector((state: RootState) => state.authToken);
  const summitName = useSelector((state: RootState) => state.summitName);
  const summitDescription = useSelector(
    (state: RootState) => state.summitDescription
  );

  const [isLoading, setIsLoading] = useState(true);
  const [alertColor, setAlertColor] = useState("green");
  const [alertString, setAlertString] = useState<string | null | undefined>("");
  const [isDiscardModalVisible, setIsDiscardModalVisible] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [skillModuleOptions, setSkillModuleOptions] = useState<any[]>([]);
  const [cohortOptions, setCohortOptions] = useState<any[]>([]);
  const [skillModules, setSkillModules] = useState<
    Array<
      RecursivePartial<SkillModuleType> &
        Pick<SkillModuleType, "activeSkillList" | "publishedSkillList">
    >
  >([]);
  const [cohorts, setCohorts] = useState<Array<RecursivePartial<CohortType>>>(
    []
  );

  const mostRecentMatchAlgorithm = useSelector(
    (state: RootState) => state.mostRecentMatchAlgorithm
  );

  const [matchAlgorithm, setMatchAlgorithm] = useState<any>(
    mostRecentMatchAlgorithm
      ? allowedMatchAlgorithmList.find(
          (algorithm) => algorithm.id === mostRecentMatchAlgorithm
        )
      : allowedMatchAlgorithmList[0]
  );

  const matchAlgorithmRef = useRef<any>();
  matchAlgorithmRef.current = matchAlgorithm;

  const [startDate, setStartDate] = useState([new Date()]);
  const [endDate, setEndDate] = useState([new Date()]);

  const now = new Date();
  const [summitTimeZone, setSummitTimeZone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const [summitTimeZoneOffset, setSummitTimeZoneOffset] = useState(
    new Date().getTimezoneOffset()
  );
  const [timeWindowIntroVisible, setTimeWindowIntroVisible] = useState(false);
  const [timeWindows, setTimeWindows] = useState<Partial<TimeWindowType>[]>([]);
  const [deactivateTimeWindows, setDeactivateTimeWindows] = useState<
    Partial<TimeWindowType>[]
  >([]);

  const [imageUrl, setImageUrl] = useState("");
  const [introVideoUrl, setIntroVideoUrl] = useState("");

  const isNew = match.params.idHash === "new";

  const [getSummitDetail, {}] = useLazyQuery(GET_SUMMIT_DETAIL, {
    fetchPolicy: "network-only", // if this isn't specified, the onCompleted will only run once, since subsequent calls will be cached
    onCompleted: ({ userSummit: data }) => {
      if (data) {
        setIsPublished(data.isPublished);
        dispatch(
          ActionCreators.setSummitName(data?.name + (isNew ? " [COPY]" : ""))
        );
        dispatch(ActionCreators.setSummitDescription(data.description));
        setStartDate(
          !match.params.cloneIdHash
            ? [new Date(Date.parse(data.startDate))]
            : [new Date()]
        );
        setEndDate(
          !match.params.cloneIdHash
            ? [new Date(Date.parse(data.endDate))]
            : [new Date()]
        );
        setSkillModules(data.skillModules);
        setCohorts(data.cohorts);
        setMatchAlgorithm(
          allowedMatchAlgorithmList.find(
            (algorithm) => algorithm.id === data.matchAlgorithm
          )
        );
        setTimeWindows(getNodes(data.timeWindows));
        setImageUrl(data.imageUrl);
        setIntroVideoUrl(data.introVideoUrl);
      } else {
        history.push("/events");
      }
      setIsLoading(false);
    },
    onError: (error) => {
      UpduoAnalytics.logEvent("app-summitdetailedit-getsummitdetail-error", {
        error: error.message,
      });
      history.push("/events");
    },
  });

  const [getCurriculum, {}] = useLazyQuery(GET_CURRICULUM, {
    fetchPolicy: "network-only", // if this isn't specified, the onCompleted will only run once, since subsequent calls will be cached
    onCompleted: ({ staffSkillModules: data }) => {
      const { edges } = data;
      if (edges.length > 0) {
        const response = edges.map((edge) => edge.node);
        const availableSkillModuleOptions = response.filter(
          (skillModule: SkillModuleType) =>
            skillModule.isActive &&
            skillModule.publishedSkillList.edges.length > 0
        );
        setSkillModuleOptions(availableSkillModuleOptions);
      }
      setIsLoading(false);
    },
    onError: () => history.goBack(),
  });

  const [getCohorts, {}] = useLazyQuery(GET_COHORTS, {
    fetchPolicy: "network-only", // if this isn't specified, the onCompleted will only run once, since subsequent calls will be cached
    onCompleted: ({ staffCohorts: data }) => {
      const { edges } = data;
      if (edges.length > 0) {
        const availableCohortOptions = edges.map((edge) => edge.node);
        setCohortOptions(availableCohortOptions);
      }
      setIsLoading(false);
    },
    onError: () => history.goBack(),
  });

  const updateTimeWindowPromise = useCallback(
    (summitId: string) => {
      Promise.all(
        [
          timeWindows.map((timeWindow) => {
            const updateTimeWindowRequest = {
              summitId,
              timeWindowId: timeWindow.id,
              startTime: timeWindow.startTime,
              endTime: timeWindow.endTime,
              text: timeWindow.text,
              order: timeWindow.order,
            };
            UpduoAnalytics.logEvent(
              "user-summit-detail-edit-update-timewindow",
              updateTimeWindowRequest
            );
            return updateTimeWindow({
              variables: { updateTimeWindowRequest },
            }) as Promise<unknown>;
          }),
          deactivateTimeWindows.map((timeWindow) => {
            UpduoAnalytics.logEvent(
              "user-summit-detail-edit-delete-timewindow",
              {
                summitId: match.params.idHash,
                timeWindowId: timeWindow.id,
                startTime: timeWindow.startTime,
                endTime: timeWindow.endTime,
                order: timeWindow.order,
              }
            );
            return deactivateTimeWindow({
              variables: {
                deactivateTimeWindowRequest: {
                  timeWindowId: timeWindow.id,
                },
              },
            }) as Promise<unknown>;
          }),
        ].flat()
      )
        .then(() => {
          setIsLoading(false);
          setAlertColor("green");
          setAlertString("Your changes are saved!");
          _handleGetSummitDetail(summitId);
        })
        .catch((error) => {
          console.log(error);
        });
    },
    [timeWindows, deactivateTimeWindows]
  );

  const [createSummit, {}] = useMutation<CreateSummitMutationResponse>(
    CREATE_SUMMIT,
    {
      onCompleted: (data) => {
        if (!data["createSummit"]?.ok) {
          UpduoAnalytics.logEvent("app-summitdetailedit-createsummit-error", {
            path: "onCompleted",
            error: data["createSummit"].reason,
          });
          setIsLoading(false);
          setAlertColor(skaleRed);
          setAlertString(data["createSummit"].reason);
        } else {
          const summitId = data["createSummit"].summitId;
          updateTimeWindowPromise(summitId);
        }
      },
      onError: (error) => {
        UpduoAnalytics.logEvent("app-summitdetailedit-createsummit-error", {
          path: "onError",
          error,
        });
        setIsLoading(false);
        setAlertColor(skaleRed);
        setAlertString(error.message);
      },
    }
  );

  const [editSummit, {}] = useMutation<EditSummitMutationResponse>(
    EDIT_SUMMIT,
    {
      onCompleted: (data) => {
        if (!data["editSummit"]?.ok) {
          UpduoAnalytics.logEvent(
            "app-summitdetailedit-editsummitdetail-error",
            {
              path: "onCompleted",
              error: data["editSummit"].reason,
            }
          );
          setIsLoading(false);
          setAlertColor(skaleRed);
          setAlertString(data["editSummit"].reason);
        } else {
          updateTimeWindowPromise(match.params.idHash);
        }
      },
      onError: (error) => {
        UpduoAnalytics.logEvent("app-summitdetailedit-editsummitdetail-error", {
          path: "onError",
          error,
        });
        setIsLoading(false);
        setAlertColor(skaleRed);
        setAlertString(error.message);
      },
    }
  );

  const [updateTimeWindow, {}] = useMutation<UpdateTimeWindowResponse>(
    UPDATE_TIME_WINDOW,
    {
      onError: (error) => {
        UpduoAnalytics.logEvent("app-summitdetailedit-updatetimewindow-error", {
          error: error.message,
        });
        setIsLoading(false);
        setAlertColor(skaleRed);
        setAlertString(error.message);
      },
    }
  );

  const [deactivateTimeWindow, {}] = useMutation<DeactivateTimeWindowResponse>(
    DEACTIVATE_TIME_WINDOW,
    {
      onError: (error) => {
        UpduoAnalytics.logEvent(
          "app-summitdetailedit-deactivatetimewindow-error",
          {
            error: error.message,
          }
        );
        setIsLoading(false);
        setAlertColor(skaleRed);
        setAlertString(error.message);
      },
    }
  );

  const _handleRetrieveCurriculum = useCallback(() => {
    setIsLoading(true);
    getCurriculum();
    getCohorts();
  }, [isNew, match.params.cloneIdHash]);

  useEffect(() => {
    // Populate summit depending on whether summit is new, clone, or existing
    // If creating a new summit
    if (isNew) {
      dispatch(
        ActionCreators.setSummitDescription(
          "In this Upduo event, you and your partners will experience deep one-on-one conversations together."
        )
      );
      // If cloning a summit
      if (match.params.cloneIdHash) {
        _handleGetSummitDetail(match.params.cloneIdHash);
      } else {
        dispatch(ActionCreators.setSummitName("New Event"));
        setIsLoading(false);
      }
    } else {
      // If editing an existing summit
      _handleGetSummitDetail(match.params.idHash);
    }
    _handleRetrieveCurriculum();
  }, [
    authToken,
    dispatch,
    isNew,
    match.params.cloneIdHash,
    match.params.idHash,
    _handleRetrieveCurriculum,
  ]);

  const _handleKeyDown = (e) => {
    const regex = /Mac/;
    const isMac = regex.exec(navigator.platform);
    if (e.keyCode === 83 && (isMac ? e.metaKey : e.ctrlKey)) {
      // Allow save keyboard shortcut to work
      e.preventDefault();
      _handleSave();
    }
  };

  useEventListener("keydown", _handleKeyDown);

  const _handleShowUnsavedWarning = useCallback(() => {
    setAlertColor(skaleRed);
    setAlertString("You have unsaved changes.");
  }, []);

  const dropdownItemHeight = 46;

  const _handleGetSummitDetail = (summitHash) => {
    setIsLoading(true);
    getSummitDetail({
      variables: { getSummitRequest: { summitId: summitHash } },
    });
  };

  const _handleSetIsPublished = () => {
    setIsPublished((previousState) => !previousState);
    _handleShowUnsavedWarning();
  };

  const _handleEditDescription = (newSummitDescription) => {
    if (newSummitDescription === summitDescription) {
      return;
    }
    _handleShowUnsavedWarning();
    dispatch(ActionCreators.setSummitDescription(newSummitDescription));
  };

  const _handleDragEnd = (result) => {
    if (!result.destination) {
      return;
    }
    // Handles correcting ordering for skillSessionSteps,
    // and does so for only the local state
    if (
      result.type === "timewindow" &&
      result.source.index !== result.destination.index
    ) {
      const newTimeWindows = reorder(
        timeWindows,
        result.source.index,
        result.destination.index
      );
      for (let k = 0; k < newTimeWindows.length; k++) {
        newTimeWindows[k].order = k;
      }
      setTimeWindows(newTimeWindows);
      _handleShowUnsavedWarning();
    }
  };

  const _handleTimeWindowRowAdd = () => {
    const now = new Date();
    const utcOffset = new Date().getTimezoneOffset();
    const utcOffsetHours = Math.floor(utcOffset / 60);
    now.setHours(now.getHours() + utcOffsetHours);
    now.setMinutes(0);

    setTimeWindows((oldTimeWindows) =>
      oldTimeWindows.concat([
        {
          __typename: "TimeWindowType",
          startTime: convertDateTypeToTimeString(now),
          endTime: convertDateTypeToTimeString(now),
          order: oldTimeWindows.length,
        },
      ])
    );
  };

  const _handleTimeWindowDelete = (timeWindowKey) => {
    if (timeWindows[timeWindowKey].id) {
      const newDeactivateTimeWindow = Object.assign(
        {},
        timeWindows[timeWindowKey]
      );
      setDeactivateTimeWindows((oldDeactivateTimeWindows) =>
        oldDeactivateTimeWindows.concat(newDeactivateTimeWindow)
      );
    }
    const newTimeWindows = [...timeWindows];
    newTimeWindows.splice(timeWindowKey, 1);
    for (let k = 0; k < newTimeWindows.length; k++) {
      newTimeWindows[k].order = k;
    }
    setTimeWindows(newTimeWindows);
  };

  const _handleSave = useCallback(() => {
    UpduoAnalytics.logEvent("user-summit-detail-save", {
      summitId: match.params.idHash,
    });
    const canEdit = Boolean(authToken);
    if (!canEdit) {
      setAlertColor(skaleRed);
      setAlertString("Topic editing is currently disabled.");
      return;
    }
    const newSummitTimeZoneOffset =
      new Date().getTimezoneOffset() - summitTimeZoneOffset;
    const newStartDate = new Date(
      startDate[0].getTime() - newSummitTimeZoneOffset * 60 * 1000
    );
    const newEndDate = new Date(
      endDate[0].getTime() - newSummitTimeZoneOffset * 60 * 1000
    );
    if (newStartDate.getTime() >= newEndDate.getTime()) {
      setAlertColor(skaleRed);
      setAlertString("Start date/time must be before end date/time.");
      return;
    }

    if (
      skillModules
        .map((skillModule) => getNodes(skillModule.publishedSkillList))
        .flat().length <= 0
    ) {
      setAlertColor(skaleRed);
      setAlertString("Events must include at least one non-empty topic.");
      return;
    }

    const skillModuleIds = skillModules.map((sm) => sm.id);
    const cohortIds = cohorts.map((sm) => sm.id);

    setIsLoading(true);

    const summitSaveObject = {
      name: summitName,
      isPublished,
      description: summitDescription,
      skillModules: skillModuleIds,
      cohorts: cohortIds,
      matchAlgorithm: matchAlgorithm.id,
      startDate: newStartDate.toISOString(),
      endDate: newEndDate.toISOString(),
      imageUrl,
      introVideoUrl,
    };
    if (isNew) {
      createSummit({
        variables: {
          createSummitMutationRequest: summitSaveObject,
        },
      });
    } else {
      editSummit({
        variables: {
          editSummitMutationRequest: Object.assign(
            { summitId: match.params.idHash },
            summitSaveObject
          ),
        },
      });
    }
  }, [
    authToken,
    summitName,
    isPublished,
    skillModules,
    cohorts,
    matchAlgorithm,
    summitDescription,
    startDate,
    endDate,
    summitTimeZoneOffset,
    timeWindows,
    imageUrl,
    introVideoUrl,
    history,
    isNew,
    match.params.idHash,
    skillModuleOptions,
  ]);

  if (!selfObject.isStaff) {
    return <Redirect to={"/"} />;
  }

  const _handleDiscardEditsButtonPress = () => {
    UpduoAnalytics.logEvent("user-summit-detail-edit-toggle", {
      summitId: match.params.idHash,
      isChanged: alertString ? true : false,
    });
    if (alertString) {
      setIsDiscardModalVisible(true);
      return;
    }
    if (isNew) {
      history.push("/events");
      return;
    }
    setAlertString("");
  };

  const _handleCancelDiscard = () => {
    UpduoAnalytics.logEvent("user-summit-detail-discard-cancel", {
      summitId: match.params.idHash,
    });
    setIsDiscardModalVisible(false);
  };

  const _handleConfirmDiscard = () => {
    UpduoAnalytics.logEvent("user-summit-detail-discard-confirm", {
      summitId: match.params.idHash,
    });
    if (isNew) {
      history.push("/events");
    } else {
      _handleGetSummitDetail(match.params.idHash);
      setAlertString("");
      setIsDiscardModalVisible(false);
    }
  };

  const inputBorderStyle: ViewStyle = {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: skaleWebBackgroundGray,
    borderStyle: "solid",
  };
  const summitInputStyles: ViewStyle = {
    ...inputBorderStyle,
    backgroundColor: skaleWebTextBackgroundLightGray,
    height: dropdownItemHeight,
  };

  if (isLoading) {
    return <LoadingView />;
  } else {
    const saveButton = (
      <Button
        marginLeft={20}
        onPress={() => {
          _handleSave();
        }}
        fontSize={16}
        text={isNew ? "Save New Event" : "Save Changes"}
        width={200}
        height={36}
        color={upduoPrimaryBlue}
        borderColor={skalePrimaryBlue}
      />
    );
    const editToggleButton = (
      <Button
        onPress={() => {
          _handleDiscardEditsButtonPress();
        }}
        text={"Discard Edits"}
        width={170}
        height={36}
        borderWidth={1}
        borderColor={skaleRed}
        color={skaleWhite}
        textColor={skaleRed}
      />
    );

    const liveButton = (
      <View
        style={{
          justifyContent: "space-around",
          flexDirection: "row",
          alignItems: "center",
          backgroundColor: skaleOrange,
          borderRadius: 3,
          width: 67,
          height: 23,
          paddingHorizontal: 6,
        }}
      >
        <div
          style={{
            color: skaleWhite,
            paddingBottom: 3,
            paddingLeft: 1,
          }}
        >
          <Icon size={12} icon={videoCamera} />
        </div>
        <Text
          isHeading={true}
          style={{ color: skaleWhite, fontSize: 12, letterSpacing: 1 }}
        >
          {"LIVE"}
        </Text>
      </View>
    );

    const summitHeading = isNew ? "Schedule New Event" : "Modify Event";
    const summitIsLive = now >= startDate[0] && now < endDate[0];
    const editControls = (
      <View style={{ flexDirection: "row" }}>
        {editToggleButton}
        {saveButton}
      </View>
    );

    // handles override styles for baseui select, Time/Date pickers
    const selectOverrides = {
      ControlContainer: {
        style: ({ $isOpen }) => ({
          backgroundColor: skaleWebTextBackgroundLightGray,
          borderColor: $isOpen ? upduoPrimaryBlue : upduoSecondLightestGray,
          borderRadius: "8px",
          borderWidth: "1px",
        }),
      },
      Popover: {
        props: {
          overrides: {
            Body: {
              style: () => ({
                borderRadius: "8px",
                boxShadow: "none",
                margin: "6px 0px 6px 0px",
              }),
            },
            Inner: {
              style: () => ({
                borderRadius: "8px",
              }),
            },
          },
        },
      },
      Dropdown: {
        style: () => ({
          borderRadius: "8px",
          border: "1px #E2E2E2 solid",
          boxShadow: "none",
          maxHeight: "200px",
        }),
      },
      DropdownListItem: {
        style: () => ({
          padding: 0,
          margin: 0,
        }),
      },
      OptionContent: {
        style: ({ $isHighlighted }) => ({
          backgroundColor: $isHighlighted ? "#F1F4FD" : skaleWhite,
          color: $isHighlighted ? upduoPrimaryBlue : upduoPrimaryDark,
          padding: "8px 8px 8px 16px",
          margin: 0,
        }),
      },

      SelectArrow: ({ $isOpen }) =>
        $isOpen ? (
          <ChevronUp color={upduoPrimaryBlue} size={24} />
        ) : (
          <ChevronDown color={upduoMediumDarkGray} size={24} />
        ),
    };

    const characterCount = (
      <View
        style={{
          alignItems: "flex-end",
        }}
      >
        <Text
          style={{
            marginBottom: 5,
            color: summitName.length === 80 ? skaleRed : skaleDarkGray,
            fontSize: 14,
          }}
        >
          {`${summitName.length}/80`}
        </Text>
      </View>
    );
    // handles override styles for baseui Time/Date pickers

    const timePickerOverrides = {
      Select: {
        props: {
          overrides: selectOverrides,
        },
      },
    };

    const datePickerOverrides = {
      Input: {
        props: {
          overrides: {
            Root: {
              style: ({ $isFocused }) => ({
                backgroundColor: skaleWebBackgroundGray,
                borderColor: $isFocused
                  ? upduoPrimaryBlue
                  : upduoSecondLightestGray,
                borderWidth: "1px",
                borderRadius: "8px",
              }),
            },
            Input: {
              style: () => ({
                backgroundColor: skaleWebTextBackgroundLightGray,
              }),
            },
          },
        },
      },
    };

    if (isLoading) {
      return <LoadingView />;
    } else
      return (
        <View
          style={{
            flex: 1,
            overflow: "scroll",
          }}
        >
          <BackExploreBar
            previousTitle={isNew ? "Events" : "Event"}
            onBack={() => {
              setIsLoading(true);
              UpduoAnalytics.logEvent("user-summit-detail-edit-back", {
                summitId: match.params.idHash,
              });

              history.push(
                isNew ? "/events" : `/events/detail/${match.params.idHash}`
              );
              setIsLoading(false);
            }}
          />
          <Text
            style={{
              marginTop: 16,
              marginBottom: 4,
              paddingHorizontal: 30,
              color: upduoPrimaryBlue,
              fontSize: 12,
              textTransform: "uppercase",
              fontWeight: "500",
            }}
          >
            {isPublished ? "Published" : "Unpublished"}
          </Text>
          <View
            style={{
              flex: 1,
              paddingTop: 0,
              paddingHorizontal: 30,
              zIndex: -95,
            }}
          >
            <View
              style={{
                zIndex: 5,
                paddingVertical: 10,
                justifyContent: "center",
              }}
            >
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "flex-start",
                  paddingHorizontal: 15,
                  justifyContent: "space-between",
                }}
              >
                <View
                  style={{
                    alignSelf: "center",
                    justifyContent: "center",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  <Text
                    style={{
                      fontSize: 24,
                      fontWeight: "bold",
                      color: upduoPrimaryBlack,
                    }}
                  >
                    {summitHeading}
                  </Text>

                  <View
                    style={{
                      flexDirection: "row",
                      marginLeft: 25,
                      paddingTop: 5,
                    }}
                  >
                    <Toggle
                      checked={isPublished}
                      onClick={_handleSetIsPublished}
                      size={12}
                    />
                    <Text
                      isHeading={true}
                      style={{
                        paddingHorizontal: 12,
                        paddingTop: 3,
                        color: isPublished
                          ? upduoPrimaryBlue
                          : upduoLighterGray,
                        fontSize: 16,
                        textTransform: "uppercase",
                      }}
                    >
                      {"Publish"}
                    </Text>
                  </View>
                  {summitIsLive ? (
                    <View
                      style={{
                        marginHorizontal: 5,
                      }}
                    >
                      {liveButton}
                    </View>
                  ) : (
                    <></>
                  )}
                </View>
                <View style={{ flexDirection: "row", alignItems: "center" }}>
                  <Text
                    style={{
                      color: alertColor,
                      marginRight: 20,
                    }}
                  >
                    {alertString}
                  </Text>
                  {alertString && alertColor === skaleRed ? editControls : null}
                </View>
              </View>
            </View>
            <View style={{ alignItems: "center", marginTop: 15 }}>
              <View
                style={{
                  width: "100%",
                }}
              >
                <View
                  style={{
                    backgroundColor: skaleWhite,
                    borderWidth: 1,
                    borderColor: skaleWebBackgroundGray,
                    padding: 20,
                    borderRadius: 10,
                    marginBottom: 20,
                  }}
                >
                  <View
                    style={{
                      marginVertical: 32,
                      marginLeft: 50,
                      marginRight: 211,
                    }}
                  >
                    <View style={{ width: "45%", marginBottom: 20 }}>
                      <Text style={styles.titleEditText}>{"Event Name"}</Text>
                      <TextInput
                        style={[
                          styles.skillInput,
                          summitInputStyles,
                          {
                            height: dropdownItemHeight,
                          },
                        ]}
                        value={summitName}
                        maxLength={80}
                        onChangeText={(text) => {
                          dispatch(ActionCreators.setSummitName(text));
                          _handleShowUnsavedWarning();
                        }}
                      />
                      {characterCount}
                    </View>
                    <View
                      style={{
                        flexDirection: "row",
                        justifyContent: "space-between",
                      }}
                    >
                      <View
                        style={{
                          width: "36.7%",
                        }}
                      >
                        <Text style={[styles.webtitleText]}>
                          {"Topic(s)"}
                          <Icon
                            size={15}
                            icon={questionCircle}
                            data-tip
                            data-for={"unpublishedSkillModules"}
                            style={{
                              marginLeft: 5,
                            }}
                          />
                        </Text>
                        <View>
                          <Select
                            searchable={true}
                            multi={true}
                            options={skillModuleOptions.filter(
                              (skillModule) => skillModule.isActive
                            )}
                            labelKey={"name"}
                            valueKey={"id"}
                            value={skillModules}
                            placeholder={""}
                            onChange={({ value }) => {
                              setSkillModules(value as SkillModuleType[]);
                              _handleShowUnsavedWarning();
                            }}
                            overrides={selectOverrides as SelectOverrides}
                          />
                        </View>
                        <View style={{ marginTop: 20 }}>
                          <Text style={styles.webtitleText}>
                            {"Lessons in this Event:"}
                          </Text>
                          {skillModules
                            .map((skillModule) =>
                              getNodes(skillModule.publishedSkillList)
                            )
                            .flat()
                            .map((item, i) => (
                              <Text style={styles.descriptionText2} key={i}>
                                {`${i + 1}. ${item.name}\n`}
                              </Text>
                            ))}
                          <View
                            style={{
                              flexDirection: "row",
                              justifyContent: "space-between",
                            }}
                          >
                            <View
                              style={{
                                flex: 1,
                                marginRight: "2vw",
                              }}
                            >
                              <Text
                                style={[
                                  styles.webtitleText,
                                  {
                                    marginTop: 17,
                                  },
                                ]}
                              >
                                {"Intro Video URL"}
                              </Text>
                              <TextInput
                                style={[
                                  styles.skillInput,
                                  summitInputStyles,
                                  { height: dropdownItemHeight },
                                ]}
                                value={introVideoUrl}
                                onChangeText={(text) => {
                                  setIntroVideoUrl(text);
                                  _handleShowUnsavedWarning();
                                }}
                              />
                            </View>
                          </View>
                        </View>
                      </View>
                      {selfObject.primaryEmail?.endsWith("@upduo.com") ||
                      selfObject.primaryEmail?.endsWith("@arcade.co") ? (
                        <View style={{ width: "36.7%" }}>
                          <Text style={styles.webtitleText}>
                            {"Match Algorithm"}
                          </Text>
                          <View style={{ marginBottom: 20 }}>
                            <Select
                              searchable={true}
                              options={allowedMatchAlgorithmList}
                              labelKey={"name"}
                              valueKey={"id"}
                              value={matchAlgorithm}
                              placeholder={""}
                              onChange={({ value }) => {
                                const selectedMatchAlgorithm = value[0];
                                if (
                                  matchAlgorithmRef.current.id !==
                                  selectedMatchAlgorithm.id
                                ) {
                                  setMatchAlgorithm(selectedMatchAlgorithm);
                                  _handleShowUnsavedWarning();
                                }
                              }}
                              overrides={selectOverrides as SelectOverrides}
                            />
                          </View>
                          <Text style={styles.webtitleText}>{"Cohort(s)"}</Text>
                          <View>
                            <Select
                              searchable={true}
                              multi={true}
                              options={cohortOptions}
                              labelKey={"name"}
                              valueKey={"id"}
                              value={cohorts}
                              placeholder={""}
                              onChange={({ value }) => {
                                setCohorts(value as CohortType[]);
                                _handleShowUnsavedWarning();
                              }}
                              overrides={selectOverrides as SelectOverrides}
                            />
                          </View>
                        </View>
                      ) : null}
                      <View style={{ width: "16%", height: 162 }}>
                        <Text style={styles.webtitleText}>{"Image"}</Text>
                        <TextInput
                          style={[
                            styles.skillInput,
                            summitInputStyles,
                            {
                              width: "72%",
                              height: "80%",
                              marginRight: 20,
                              marginBottom: 0,
                            },
                          ]}
                          multiline={true}
                          value={imageUrl}
                          onChangeText={(text) => {
                            setImageUrl(text);
                            _handleShowUnsavedWarning();
                          }}
                        />
                      </View>
                    </View>
                    <View style={{ width: "25%" }}>
                      <View style={{ flexDirection: "row" }}>
                        <View>
                          <Text
                            style={[
                              styles.webtitleText,
                              {
                                marginTop: 20,
                                marginBottom: 0,
                              },
                            ]}
                          >
                            {"Start Date/Time"}
                          </Text>
                          <View
                            style={{
                              flex: 1,
                              flexDirection: "row",
                              marginTop: 16,
                            }}
                          >
                            <View style={{ width: "50%", marginRight: 8 }}>
                              <DatePicker
                                value={startDate}
                                onChange={({ date }) => {
                                  const newDate = Array.isArray(date)
                                    ? date
                                    : [date];
                                  newDate[0].setHours(startDate[0].getHours());
                                  newDate[0].setMinutes(
                                    startDate[0].getMinutes()
                                  );
                                  setStartDate(newDate);
                                  _handleShowUnsavedWarning();
                                }}
                                overrides={datePickerOverrides}
                              />
                            </View>
                            <View style={{ width: "50%" }}>
                              <TimePicker
                                value={startDate[0]}
                                onChange={(date) => {
                                  if (date) {
                                    setStartDate([date]);
                                    _handleShowUnsavedWarning();
                                  }
                                }}
                                step={1800}
                                creatable
                                overrides={timePickerOverrides}
                              />
                            </View>
                          </View>
                        </View>
                        <View>
                          <Text
                            style={[
                              styles.webtitleText,
                              {
                                marginTop: 20,
                                paddingLeft: 48,
                                marginBottom: 0,
                              },
                            ]}
                          >
                            {"End Date/Time"}
                          </Text>
                          <View
                            style={{
                              flex: 1,
                              flexDirection: "row",
                              marginTop: 16,
                              paddingLeft: 48,
                            }}
                          >
                            <View style={{ width: "50%", marginRight: 8 }}>
                              <DatePicker
                                value={endDate}
                                onChange={({ date }) => {
                                  const newDate = Array.isArray(date)
                                    ? date
                                    : [date];
                                  newDate[0].setHours(endDate[0].getHours());
                                  newDate[0].setMinutes(
                                    endDate[0].getMinutes()
                                  );
                                  setEndDate(newDate);
                                  _handleShowUnsavedWarning();
                                }}
                                overrides={datePickerOverrides}
                              />
                            </View>
                            <View style={{ width: "50%" }}>
                              <TimePicker
                                value={endDate[0]}
                                onChange={(date) => {
                                  if (date) {
                                    setEndDate([date]);
                                    _handleShowUnsavedWarning();
                                  }
                                }}
                                step={1800}
                                creatable
                                overrides={timePickerOverrides}
                              />
                            </View>
                          </View>
                        </View>
                      </View>
                    </View>
                    <View
                      style={{
                        flexDirection: "row",
                        justifyContent: "space-between",
                      }}
                    >
                      <View
                        style={{
                          width: "37%",
                        }}
                      >
                        <Text
                          style={[
                            styles.webtitleText,
                            {
                              marginTop: 20,
                              marginBottom: 0,
                            },
                          ]}
                        >
                          {"Timezone"}
                        </Text>
                        <View
                          style={{
                            flex: 1,
                            flexDirection: "row",
                            marginTop: 16,
                          }}
                        >
                          <View
                            style={{
                              width: "100%",
                            }}
                          >
                            <TimezonePicker
                              value={summitTimeZone}
                              onChange={(timezone) => {
                                if (timezone?.offset) {
                                  setSummitTimeZone(timezone.id);
                                  setSummitTimeZoneOffset(timezone.offset);
                                  _handleShowUnsavedWarning();
                                }
                              }}
                              overrides={timePickerOverrides}
                            />
                          </View>
                        </View>
                      </View>
                    </View>
                  </View>
                  <View
                    style={{
                      marginLeft: 50,
                      marginTop: 40,
                    }}
                  >
                    <View
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        zIndex: 1,
                      }}
                    >
                      <Text>{"Time Windows"}</Text>
                      <TouchableOpacity
                        style={{
                          marginLeft: 6,
                        }}
                        {...Platform.select({
                          web: {
                            onMouseEnter: () => {
                              setTimeWindowIntroVisible(true);
                              UpduoAnalytics.logEvent(
                                "user-summit-detail-edit-hover-timewindow-info-icon"
                              );
                            },
                            onMouseLeave: () => {
                              setTimeWindowIntroVisible(false);
                            },
                          },
                        })}
                      >
                        <Text
                          style={{
                            color: upduoPrimaryBlue,
                          }}
                        >
                          <Icon size={20} icon={info} />
                        </Text>
                        <TimeWindowIntroModal
                          isVisible={timeWindowIntroVisible}
                        />
                      </TouchableOpacity>
                    </View>
                    <DragDropContext onDragEnd={_handleDragEnd}>
                      <Droppable
                        droppableId={"timewindow-droppable"}
                        type={"timewindow"}
                      >
                        {(provided) => (
                          <div ref={provided.innerRef}>
                            <View style={{ minHeight: 50 }}>
                              {timeWindows.map((timeWindow, index) => {
                                return (
                                  <TimeWindowEditRow
                                    key={timeWindow.id}
                                    timeWindowKey={index}
                                    summitInputStyles={summitInputStyles}
                                    dropdownItemHeight={dropdownItemHeight}
                                    timePickerOverrides={timePickerOverrides}
                                    _handleShowUnsavedWarning={
                                      _handleShowUnsavedWarning
                                    }
                                    timeWindow={timeWindow}
                                    onChangeTimeWindow={(newTimeWindow) => {
                                      const newTimeWindows = [...timeWindows];
                                      newTimeWindows[index] = newTimeWindow;
                                      setTimeWindows(newTimeWindows);
                                    }}
                                    onDelete={_handleTimeWindowDelete}
                                  />
                                );
                              })}
                              {provided.placeholder}
                            </View>
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                    <Button
                      width={205}
                      height={32}
                      marginTop={18}
                      padding={12}
                      color={upduoPrimaryBlue}
                      onPress={() => {
                        _handleTimeWindowRowAdd();
                        _handleShowUnsavedWarning();
                      }}
                      text={"+ ADD TIME WINDOW"}
                      disabledColor={skaleWebBorderGray}
                      disabledTextColor={skaleWhite}
                    />
                  </View>

                  <View style={{ marginLeft: 50, marginRight: 444 }}>
                    <Text
                      style={[
                        styles.webtitleText,
                        {
                          marginTop: 41,
                          marginBottom: 0,
                        },
                      ]}
                    >
                      {"Description"}
                    </Text>
                    <View style={{ marginTop: 16, marginBottom: 0 }}>
                      <DescriptionTextField
                        description={summitDescription}
                        onEdit={(newSummitDescription) => {
                          _handleEditDescription(newSummitDescription);
                        }}
                        webBackground={true}
                        isHtml={false}
                      />
                    </View>
                  </View>
                </View>
              </View>
            </View>
          </View>
          <ReactTooltip
            id={"unpublishedSkillModules"}
            type={"dark"}
            effect={"solid"}
            place={"right"}
          >
            <Text style={{ color: skaleWhite, fontSize: 13 }}>
              {"Topics with no published lessons are not listed"}
            </Text>
          </ReactTooltip>
          <ConfirmDiscardModal
            visible={isDiscardModalVisible}
            onCancelDiscard={_handleCancelDiscard}
            onConfirmDiscard={_handleConfirmDiscard}
          />
          <Prompt
            when={alertColor === skaleRed && !!alertString}
            message={
              "You have unsaved changes, are you sure you want to leave?"
            }
          />
        </View>
      );
  }
}
